import './sampler-banner.css'
import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import { VIEW_TYPES, DEFAULT_MEAL_PLAN_URL } from '../../scripts/lib/constants.js'
import CustomerService from '../../scripts/lib/customer-service.js'

class SamplerBanner extends HTMLElement {
  #modal = null
  #boxState = null
  #boundHandleEligibilityChange = null
  #boundHandleSamplerDiscount = null
  #boundHandleClose = null
  #boundHandleClaim = null
  #closeButtons = []
  #claimButton = null

  async connectedCallback() {
    this.#boxState = BoxState.getInstance()

    if (this.#boxState.samplerBannerClosed) {
      this.remove()
      return
    }

    this.#boundHandleEligibilityChange = (eligible) => {
      this.dataset.eligible = eligible.toString()

      if (!eligible) {
        this.#updateWelcomeMessage()
      }
    }

    this.#boundHandleSamplerDiscount = (info) => {
      if (info) {
        this.#updateDiscountDisplay(info.percentageOff, info.code)
      }
    }

    this.#boundHandleClose = () => {
      this.#boxState.samplerBannerClosed = true
      this.remove()
    }

    this.#boundHandleClaim = async () => {
      await this.#handleClaim()
    }

    const currentEmail = this.#boxState.getCurrentEmail()
    const eligible = await this.#boxState.checkEligibility(currentEmail)
    this.dataset.eligible = eligible.toString()

    if (!eligible && currentEmail) {
      this.#updateWelcomeMessage()
    }

    this.setupEventListeners()
    this.hidden = false
  }

  disconnectedCallback() {
    this.removeEventListeners()
    this.#boxState?.unsubscribe(EVENT_TYPES.ELIGIBILITY_CHANGE, this.#boundHandleEligibilityChange)
    this.#boxState?.unsubscribe(EVENT_TYPES.SAMPLER_DISCOUNT_CHANGE, this.#boundHandleSamplerDiscount)
  }

  setupEventListeners() {
    this.#boxState.subscribe(EVENT_TYPES.ELIGIBILITY_CHANGE, this.#boundHandleEligibilityChange)
    this.#boxState.subscribe(EVENT_TYPES.SAMPLER_DISCOUNT_CHANGE, this.#boundHandleSamplerDiscount)

    const existingInfo = this.#boxState.samplerDiscountInfo
    if (existingInfo) {
      this.#updateDiscountDisplay(existingInfo.percentageOff, existingInfo.code)
    }

    this.#closeButtons = Array.from(this.querySelectorAll('.sampler-banner__close'))
    this.#closeButtons.forEach(btn => {
      btn.addEventListener('click', this.#boundHandleClose)
    })

    this.#claimButton = this.querySelector('.sampler-banner__claim')
    this.#claimButton?.addEventListener('click', this.#boundHandleClaim)
  }

  removeEventListeners() {
    this.#closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.#boundHandleClose)
    })

    this.#claimButton?.removeEventListener('click', this.#boundHandleClaim)
  }

  #showModal() {
    if (!this.#modal) {
      this.#modal = document.querySelector('sampler-modal')
    }
    this.#modal?.removeAttribute('hidden')
    this.#modal?.show()
  }

  async #handleClaim() {
    const currentEmail = this.#boxState.getCurrentEmail()

    if (currentEmail) {
      const eligible = this.#boxState.eligibility
      const pets = this.#boxState.petProfiles || []

      if (eligible) {
        const discountInfo = this.#boxState.samplerDiscountInfo
        const defaultCode = this.#boxState.boxConfig?.subscription?.samplerDiscountCode

        const isAffiliate = discountInfo?.code &&
          (!defaultCode || discountInfo.code.toUpperCase() !== defaultCode.toUpperCase())
        if (isAffiliate) {
          try {
            const rootPath = window.Shopify?.routes?.root || '/'
            const response = await fetch(rootPath + 'discount/' + encodeURIComponent(discountInfo.code))
            if (!response.ok) {
              console.warn('[SamplerBanner] Affiliate discount not applied - user may need to enter code manually:', response.status)
            }
          } catch (error) {
            console.warn('[SamplerBanner] Affiliate discount not applied - user may need to enter code manually:', error)
          }
        }

        if (pets.length === 0) {
          this.#boxState.setSamplerStep('started')
          const mealPlanUrl = this.dataset.mealPlanUrl || DEFAULT_MEAL_PLAN_URL
          window.location.href = mealPlanUrl
        } else {
          this.#boxState.setSamplerStep('recipe')
          this.#boxState.activeView = VIEW_TYPES.SAMPLER
        }
      }
    } else {
      this.#showModal()
    }
  }

  #updateWelcomeMessage() {
    const welcomeMessage = this.querySelector('.sampler-banner__welcome')
    if (!welcomeMessage) return

    const customerService = CustomerService.getInstance()
    const customerName = customerService.getCustomerName()

    if (customerName) {
      welcomeMessage.textContent = `Welcome back, ${customerName}! Thanks for returning to Viva.`
    }
  }

  #updateDiscountDisplay(percentageOff, code) {
    const titleEl = this.querySelector('[data-sampler-title]')
    const textEl = this.querySelector('[data-sampler-text]')

    if (percentageOff && titleEl) {
      const defaultCode = this.#boxState.boxConfig?.subscription?.samplerDiscountCode
      const isAffiliate = code &&
        (!defaultCode || code.toUpperCase() !== defaultCode.toUpperCase())
      titleEl.textContent = isAffiliate
        ? `${percentageOff}% OFF Sampler Box with code ${code}!`
        : `${percentageOff}% OFF Sampler Box!`
    }
    if (textEl) {
      textEl.textContent = 'New To Viva? Get a sampler for taste testing before you subscribe!'
    }
  }
}

if (!customElements.get('sampler-banner')) {
  customElements.define('sampler-banner', SamplerBanner)
}